import { useEffect, useState } from 'react';
import { TextField, Button, FormControl, InputLabel, Select, MenuItem, FormHelperText, Checkbox, FormControlLabel, Grid, Tabs, Tab, Box, Typography, Divider, Stack, InputAdornment, Tooltip, IconButton } from '@mui/material';
import { getData, postData } from '../../services/base.service';
import { requiredFieldsReferenceForm, ReferenceAvailableOptionsType, ReferenceDetailType, LinkedExpertType, AvailableExpertType, LinkedPartnerType, AvailablePartnerType } from '../../types/dataTypes';
import AutocompleteComponent from '../Shared/AutocompleteComponent';
import { Add, Apartment, AutoStories, Close, Groups, Home, Info, List, MultipleStop, Numbers, People, Person, Public, Save } from '@mui/icons-material';
import { Edit, Lock } from '@mui/icons-material';
import React from 'react';
import ReferenceVersion from './ReferenceVersion';
import ReferenceDocumentsTab from './ReferenceDocumentsTab';
import EditableDataGrid from '../Shared/EditableDataGrid';
import { referenceExpertGridDefinition, referencePartnerGridDefinition } from '../../utils/consts';
import { useNotification } from '../Shared/NotificationProvider';


type ReferenceFormProps = {
    setLoading: Function,
    availableOptions: ReferenceAvailableOptionsType,
    formData: ReferenceDetailType[],
    type: string,
    selectedOptions: ReferenceAvailableOptionsType,
    setSelectedOptions: Function
    setReload: Function
    handleClose: Function
}

// Row shape used by the Experts EditableDataGrid. The grid needs a stable `id` per row
// for selection/dedup; expertID is used for that (mirroring how the Partner Collaboration
// History grid keys its rows by ProjectID rather than the bridge row's own surrogate key),
// while `linkID` carries bridge_experts_references.ID so update/remove calls can target it.
type ExpertLinkRow = {
    id: number; // expertID
    linkID?: number; // bridge_experts_references.ID, absent until the link is first saved
    name: string;
    position: string;
    ratePerDay: number | string | null;
    daysWorked: number | string | null;
    source: string;
    partnerID?: number | string | null;
    partnerName?: string | null;
    cvlink: string | null; // latest CV filename from ExpertWebView, used by the "View CV" action
};

// Row shape used by the Partners EditableDataGrid. Mirrors ExpertLinkRow's id/linkID split:
// `id` is the partnerID (stable per-row key, also used by ReferencePartnerActionsCell to
// link out to /partners/:partnerID), while `linkID` carries bridge_partners_references.ID
// so remove calls can target it. No share/comment fields - see LinkedPartnerType.
type PartnerLinkRow = {
    id: number; // partnerID
    linkID?: number; // bridge_partners_references.ID, absent until the link is first saved
    name: string;
};

function ReferenceForm({ setLoading, availableOptions, formData, type, selectedOptions, setSelectedOptions, setReload, handleClose }:
    ReferenceFormProps) {
    const [tabValue, setTabValue] = useState(0);
    const [isEditMode, setIsEditMode] = useState(type === 'add');
    const [saveFormData, setSaveFormData] = useState(false);
    const [currentFormData, setCurrentFormData] = useState<ReferenceDetailType>(formData.find(x => x.variant_name === 'Englisch') || formData[0]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isNewVariant, setIsNewVariant] = useState(false);
    const [variantSelected, setVariantSelected] = useState('Englisch');
    const [linkedExperts, setLinkedExperts] = useState<ExpertLinkRow[]>([]);
    const [linkedPartners, setLinkedPartners] = useState<PartnerLinkRow[]>([]);
    // Partners linked to this reference = the options for an expert's "via partner" select.
    const [partnerOptions, setPartnerOptions] = useState<LinkedPartnerType[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const notify = useNotification();

     useEffect(() => {
        console.log('FormData changed:', formData);
        const englishVariant = formData.find(x => x.variant_name === 'Englisch') || formData[0];
        if (englishVariant) {
            // Clean null values from the data
            const cleanedData = { ...englishVariant };
            Object.keys(cleanedData).forEach(key => {
                if (cleanedData[key as keyof ReferenceDetailType] === null) {
                    (cleanedData as any)[key] = '';
                }
            });
            setCurrentFormData(cleanedData);
            setVariantSelected('Englisch');
            
            // Reset edit mode when switching items
            setIsEditMode(type === 'add');
            
            // Reset form validation state
            setSaveFormData(false);
            
            // Reset variant state
            setIsNewVariant(false);
            
            // Close drawer if open
            setDrawerOpen(false);
            
            // Reset tab to first tab
            setTabValue(0);
        }
    }, [formData, type]);

    const openDrawer = () => {
        setDrawerOpen(true);
    };

    // Function to close the drawer
    const closeDrawer = () => {
        setDrawerOpen(false);
    };

    const toggleEditMode = () => {
        ;
        setIsEditMode(!isEditMode);
        console.log(variantSelected)
        //notify the user that the form is in edit mode
        notify(isEditMode ? 'Form is now in view mode. No changes possible now.' :
            'Form is now in edit mode. You can now edit the form and dont forget to hit save in the end.', 'info');
    };

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        setCurrentFormData({ ...currentFormData, [name]: value });
    };

    const handleCheckboxChange = (event: any) => {
        const { name, checked } = event.target;
        setCurrentFormData({ ...currentFormData, [name]: checked });
    };

    const handleTabChange = (event: any, newValue: any) => {
        setTabValue(newValue);
    };

    const handleClientChange = (event: any, newValue: any) => {
        setSelectedOptions((prevData: ReferenceAvailableOptionsType) => ({ ...prevData, clients: newValue }));
    };

    const handleCountryChange = (event: any, newValue: any) => {
        setSelectedOptions((prevData: ReferenceAvailableOptionsType) => ({ ...prevData, countries: newValue }));
    }

    const handleCountryHomepageChange = (event: any, newValue: any) => {
        setSelectedOptions((prevData: ReferenceAvailableOptionsType) => ({ ...prevData, countries_homepage: newValue }));
    }

    const handleFoSChange = (event: any, newValue: any) => {
        setSelectedOptions((prevData: ReferenceAvailableOptionsType) => ({ ...prevData, fieldsofServices: newValue }));
    }

    const handleSave = async () => {
        // Guards against a double-click (or an impatient second click while the network is
        // slow) firing two save requests back to back - the Save button is also disabled while
        // isSaving is true, but the state guard here covers it even if a click sneaks through
        // before the re-render disables the button.
        if (isSaving) return;
        setIsSaving(true);
        setLoading(true);
        //concat the selected options to the form data
        if (selectedOptions.countries.length > 0) currentFormData.Country = selectedOptions.countries.join(', ');
        if (selectedOptions.clients.length > 0) currentFormData.short = selectedOptions.clients.join(', ');
        if (selectedOptions.fieldsofServices.length > 0) currentFormData.FieldOfService = selectedOptions.fieldsofServices.join('; ');
        if (selectedOptions.countries_homepage.length > 0) currentFormData.country_homepage = selectedOptions.countries_homepage.join(', ');

        // check if required fields are filled
        if (!formvalidityCheck(currentFormData)) {
            notify('Please fill in all required fields. Check the red marked fields. Please also consider the Text Tabs.', 'warning');
            setSaveFormData(true);
            setLoading(false);
            setIsSaving(false);
            return;
        }

        try {
            //save the form data
            const api_type = isNewVariant ? 'newVariant' : variantSelected !== 'Englisch' ? 'updateVariant' : type;
            const resp = await postData(`references/${api_type}`, { formData: currentFormData })
            notify(resp.message, resp.statusCode === 200 ? 'success' : 'info');
            if (resp.statusCode === 200){
                setLoading(false);
                window.location.reload();
            }
            else {
                notify('Error saving form data. Please try again.', 'error');
                setLoading(false);
                setIsSaving(false);
            }
        } catch (error: any) {
            notify(error?.message || 'Error saving form data. Please try again.', 'error');
            setLoading(false);
            setIsSaving(false);
        }
    }

    const handleVersionChange = (event: any) => {
        const { value } = event.target;
        setCurrentFormData(formData.find(x => x.variant_name === value) || formData[0]);
        setVariantSelected(value);
    }

    const toExpertLinkRow = (expert: LinkedExpertType): ExpertLinkRow => ({
        id: expert.expertID,
        linkID: expert.id,
        name: expert.name,
        position: expert.position,
        ratePerDay: expert.ratePerDay,
        daysWorked: expert.daysWorked,
        source: expert.source,
        partnerID: expert.partnerID ?? null,
        partnerName: expert.partnerName,
        cvlink: expert.cvlink,
    });

    const loadLinkedExperts = async (projectID: number) => {
        const linked = await getData(`references/experts/${projectID}`);
        setLinkedExperts((linked as LinkedExpertType[]).map(toExpertLinkRow));
        try {
            setPartnerOptions((await getData(`references/partners/${projectID}`)) as LinkedPartnerType[]);
        } catch (error) {
            console.error('Error loading partner options:', error);
            setPartnerOptions([]);
        }
    };

    // Dependent "Partner" select in the expert add/edit dialog: only for source = Partner,
    // options = partners linked to this reference (no free text).
    const expertFieldRenderers = {
        partnerID: ({ label, values, setValue }: any) => {
            if (values.source !== 'Partner') return null;
            return (
                <FormControl key="partnerID" fullWidth margin="normal" disabled={partnerOptions.length === 0}>
                    <InputLabel shrink>{label}</InputLabel>
                    <Select
                        notched
                        label={label}
                        displayEmpty
                        value={values.partnerID || ''}
                        onChange={(e) => setValue('partnerID', e.target.value === '' ? null : e.target.value)}
                    >
                        <MenuItem value=""><em>— none —</em></MenuItem>
                        {partnerOptions.map((p) => (
                            <MenuItem key={p.partnerID} value={p.partnerID}>
                                {p.name}{p.comment ? ` (${p.comment})` : ''}
                            </MenuItem>
                        ))}
                    </Select>
                    {partnerOptions.length === 0 && <FormHelperText>Link the partner to this reference first</FormHelperText>}
                </FormControl>
            );
        },
    };

    // Search-as-you-type for the "add expert" picker: GET api/experts/availableforreference/:refId
    // now takes a `search` query param and returns only a small, already-filtered/capped set of
    // matches, instead of the full (potentially huge) non-linked experts table. Normally the user
    // already knows the expert's name, so there's no need to preload everyone up front anymore.
    const searchAvailableExperts = async (query: string): Promise<AvailableExpertType[]> => {
        const projectID = currentFormData?.ProjectID;
        if (!projectID) return [];
        const results = await getData(`experts/availableforreference/${projectID}?search=${encodeURIComponent(query)}`);
        return results as AvailableExpertType[];
    };

    // Load the Experts tab data (linked experts only; available experts are now resolved
    // on demand via searchAvailableExperts as the user types in the picker).
    useEffect(() => {
        if (type === 'update' && currentFormData?.ProjectID && tabValue === 3) {
            loadLinkedExperts(currentFormData.ProjectID);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, currentFormData?.ProjectID, type]);

    const toPartnerLinkRow = (partner: LinkedPartnerType): PartnerLinkRow => ({
        id: partner.partnerID,
        linkID: partner.id,
        name: partner.name,
    });

    const loadLinkedPartners = async (projectID: number) => {
        const linked = await getData(`references/partners/${projectID}`);
        setLinkedPartners((linked as LinkedPartnerType[]).map(toPartnerLinkRow));
    };

    // Search-as-you-type for the "add partner" picker: GET api/partners/availableforreference/:refId
    // takes a `search` query param and returns only a small, already-filtered/capped set of
    // matches - mirrors searchAvailableExperts above so the two "add via search" pickers feel
    // the same. Unlike the experts endpoint, this one always requires a search term.
    const searchAvailablePartners = async (query: string): Promise<AvailablePartnerType[]> => {
        const projectID = currentFormData?.ProjectID;
        if (!projectID) return [];
        const results = await getData(`partners/availableforreference/${projectID}?search=${encodeURIComponent(query)}`);
        return results as AvailablePartnerType[];
    };

    // Load the Partners tab data (linked partners only; available partners are resolved on
    // demand via searchAvailablePartners as the user types in the picker).
    useEffect(() => {
        if (type === 'update' && currentFormData?.ProjectID && tabValue === 4) {
            loadLinkedPartners(currentFormData.ProjectID);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, currentFormData?.ProjectID, type]);

    // EditableDataGrid mutates its formData/dataPath in local state only; this wrapper
    // diffs the previous linked-experts list against the new one and immediately calls
    // the add/update/remove expert-reference-link endpoints, then refreshes from the
    // server so `linkID` stays accurate for subsequent edits.
    const handleExpertsChange = async (newWrapper: { linkedExperts: ExpertLinkRow[] }) => {
        const projectID = currentFormData?.ProjectID;
        if (!projectID) return;

        const previousList = linkedExperts;
        const newList = newWrapper.linkedExperts || [];

        const additions = newList.filter((row) => !previousList.some((prev) => prev.id === row.id));
        const removals = previousList.filter((prev) => !newList.some((row) => row.id === prev.id));
        const updates = newList.filter((row) => {
            const previous = previousList.find((prev) => prev.id === row.id);
            return previous && previous.linkID !== undefined && (
                previous.position !== row.position ||
                String(previous.ratePerDay ?? '') !== String(row.ratePerDay ?? '') ||
                String(previous.daysWorked ?? '') !== String(row.daysWorked ?? '') ||
                previous.source !== row.source ||
                (previous.partnerID ?? null) !== (row.partnerID || null)
            );
        });

        setLoading(true);
        try {
            for (const removal of removals) {
                if (removal.linkID !== undefined) {
                    await postData('references/experts/remove', { id: removal.linkID });
                }
            }
            for (const addition of additions) {
                await postData('references/experts/add', {
                    refId: projectID,
                    expertID: addition.id,
                    position: addition.position || undefined,
                    ratePerDay: addition.ratePerDay !== '' && addition.ratePerDay !== null ? Number(addition.ratePerDay) : undefined,
                    daysWorked: addition.daysWorked !== '' && addition.daysWorked !== null ? Number(addition.daysWorked) : undefined,
                    source: addition.source || undefined,
                    partnerID: addition.source === 'Partner' && addition.partnerID ? Number(addition.partnerID) : undefined,
                });
            }
            for (const update of updates) {
                await postData('references/experts/update', {
                    id: update.linkID,
                    position: update.position || '',
                    ratePerDay: update.ratePerDay !== '' && update.ratePerDay !== null ? Number(update.ratePerDay) : undefined,
                    daysWorked: update.daysWorked !== '' && update.daysWorked !== null ? Number(update.daysWorked) : undefined,
                    source: update.source || undefined,
                    // null clears the partner (source switched to Integration or "none" picked)
                    partnerID: update.source === 'Partner' && update.partnerID ? Number(update.partnerID) : null,
                });
            }

            await loadLinkedExperts(projectID);
        } catch (error) {
            console.error('Error updating linked experts:', error);
            notify('Error updating linked experts. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Mirrors handleExpertsChange above, but simpler: since the Partners tab has no
    // editable per-link fields (no position/rate/days/source-equivalent), only additions
    // and removals are diffed - there is no "updates" case.
    const handlePartnersChange = async (newWrapper: { linkedPartners: PartnerLinkRow[] }) => {
        const projectID = currentFormData?.ProjectID;
        if (!projectID) return;

        const previousList = linkedPartners;
        const newList = newWrapper.linkedPartners || [];

        const additions = newList.filter((row) => !previousList.some((prev) => prev.id === row.id));
        const removals = previousList.filter((prev) => !newList.some((row) => row.id === prev.id));

        setLoading(true);
        try {
            for (const removal of removals) {
                if (removal.linkID !== undefined) {
                    await postData('references/partners/remove', { id: removal.linkID });
                }
            }
            for (const addition of additions) {
                await postData('references/partners/add', {
                    refId: projectID,
                    partnerID: addition.id,
                });
            }

            await loadLinkedPartners(projectID);
        } catch (error) {
            console.error('Error updating linked partners:', error);
            notify('Error updating linked partners. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const formvalidityCheck = (details: ReferenceDetailType): boolean => {
        let requiredFieldList = requiredFieldsReferenceForm;
        //add a conditional check for field country_homepage if homepage is checked
        if (currentFormData.Homepage === true) {
            requiredFieldList.push('country_homepage');
        }
        if (currentFormData.Homepage === false && requiredFieldList.includes('country_homepage')) {
            //remove the field from the required fields list if it is not required
            requiredFieldList = requiredFieldList.filter((field: any) => field !== 'country_homepage');
        }
        //check if all required fields are filled via a loop over the required fields and check if they are not undefined or empty
        //if they are, return false and log the error
        for (const field of requiredFieldList) {
            if (details[field as keyof ReferenceDetailType] === undefined || details[field as keyof ReferenceDetailType] === '') {
                console.error(`Validation error: ${field} is required.`);
                return false;
            }
        }
        return true;
    }

    return (
        <div>
            <Grid container spacing={2} sx={{
                borderBottom: 1,
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                background: "white",
                zIndex: 1100,
                boxShadow: 1
            }}>

                <Grid container alignItems="center">
                    <Grid item xs={4} md={4} style={{ display: 'flex', alignItems: 'center', padding: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {type === 'add' ? (
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<Save />}
                                onClick={() => handleSave()}
                                disabled={isSaving}
                            >
                                Save
                            </Button>
                            ) : (
                            <>
                                <Button
                                variant="outlined"
                                startIcon={isEditMode ? <Lock /> : <Edit />}
                                onClick={toggleEditMode}
                                color="primary"
                                >
                                {isEditMode ? 'Lock' : 'Edit'}
                                </Button>
                                {(type === 'update' && isEditMode) && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<Save />}
                                    onClick={() => handleSave()}
                                    disabled={isSaving}
                                >
                                    {isNewVariant ? 'Save new Variant' : variantSelected !== 'Englisch' ? 'Update Variant' : 'Save'}
                                </Button>
                                )}
                            </>
                            )}
                        </div>
                        </Grid>

                    <Grid item xs={4} md={4} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 10 }}>
                        {type === 'update' && (
                            <Typography variant="h6" color="primary" textAlign="center">
                                {currentFormData?.ProjectNo + ' - ' +  currentFormData.variant_name}
                            </Typography>
                        )}
                        {type === 'add' && (
                            <Typography variant="h6" color="textSecondary" textAlign="center">
                                New Reference Form
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={4} md={4} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: 10 }}>
                        {type === 'update' && (
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<MultipleStop />}
                                onClick={() => openDrawer()}
                            >
                                Select Variant
                            </Button>
                        )}                                                                        
                        {type === 'add' && (
                            <IconButton onClick={() => handleClose()}>
                                <Close />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
                        <Tab label="Project" {...a11yProps(0)} />
                        <Tab label="Text Long" {...a11yProps(1)} />
                        <Tab label="Text Short" {...a11yProps(2)} />
                        <Tab label="Experts" {...a11yProps(3)} disabled={type !== 'update'} />
                        <Tab label="Partners" {...a11yProps(4)} disabled={type !== 'update'} />
                        <Tab label="Documents" {...a11yProps(5)} disabled={type !== 'update'} />
                    </Tabs>
                </Grid>
            </Grid>
            <TabPanel value={tabValue} index={0}>
                <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }} paddingTop={2}>
                    <Grid item md={12}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <AutoStories />
                            <Typography variant="h6">General</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField InputLabelProps={{ shrink: true }} label="Project Number" name="ProjectNo" value={currentFormData.ProjectNo || ''}
                            onChange={handleInputChange} required InputProps={{ readOnly: !isEditMode }} error={saveFormData && currentFormData.ProjectNo === ''}
                            disabled={type==='update'} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField InputLabelProps={{ shrink: true }} style={{ width: '100%' }} label="Project Title" name="ProjectTitle"
                            value={currentFormData?.ProjectTitle || ''} onChange={handleInputChange} required multiline rows={3} InputProps={{ readOnly: !isEditMode }}
                            error={saveFormData && currentFormData.ProjectTitle === ''} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField InputLabelProps={{ shrink: true }} style={{ width: '100%' }} label="Project Title Short"
                            name="ProjectTitle_short" value={currentFormData?.ProjectTitle_short || ''} onChange={handleInputChange}
                            multiline rows={3} InputProps={{ readOnly: !isEditMode }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField InputLabelProps={{ shrink: true }} label="Responsible (Intials)" name="zuständig"
                            value={currentFormData?.zuständig || ''} onChange={handleInputChange} InputProps={{ readOnly: !isEditMode }} 
                            disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel control={<Checkbox size='small' checked={currentFormData?.INTEE} indeterminate={currentFormData?.INTEE === undefined}
                            style={{ color: saveFormData && currentFormData.INTEE === undefined ? 'red' : 'inherit' }}
                            onChange={handleCheckboxChange} name="INTEE" />} label="INTEE Project" required disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Divider style={{ color: 'lightgrey' }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Numbers />
                            <Typography variant="h6">Date and Project Values</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <TextField label="Start Date" value={currentFormData?.StartDate || ''} onChange={handleInputChange}
                            type="date" InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditMode }} name='StartDate'
                            required error={saveFormData && currentFormData.StartDate === ''} disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <TextField label="Completion Date" value={currentFormData?.CompletionDate || ''} onChange={handleInputChange}
                            type="date" InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditMode }} name='CompletionDate'
                            required error={saveFormData && currentFormData.CompletionDate === ''} disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <TextField
                            name="ProjectValueEuro"
                            InputLabelProps={{ shrink: true }}
                            value={currentFormData?.ProjectValueEuro || ''}
                            onChange={handleInputChange}
                            required
                            label="Project Value Euro"
                            type="number"
                            disabled={variantSelected !== 'Englisch' && type==='update'}
                            InputProps={{
                                readOnly: !isEditMode,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title={
                                            <React.Fragment>
                                                Consider Exchange rate from
                                                <a href="https://www.oanda.com/currency-converter/de/?from=EUR&to=USD&amount=1" target="_blank" rel="noopener noreferrer">
                                                    www.oanda.com
                                                </a>
                                                and use the Projects Start Date as reference
                                            </React.Fragment>
                                        }>
                                            <IconButton>
                                                <Info />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                                inputProps: {
                                    pattern: "\\d*", // only allow digits
                                },
                            }}
                            error={saveFormData && currentFormData.ProjectValueEuro === ''}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <TextField
                            name="ProjectValueDollar"
                            InputLabelProps={{ shrink: true }}
                            value={currentFormData?.ProjectValueDollar || ''}
                            onChange={handleInputChange}
                            required
                            label="Project Value Dollar"
                            type="number"
                            disabled={variantSelected !== 'Englisch' && type==='update'}
                            InputProps={{
                                readOnly: !isEditMode, endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title={
                                            <React.Fragment>
                                                Consider Exchange rate from
                                                <a href="https://www.oanda.com/currency-converter/de/?from=EUR&to=USD&amount=1" target="_blank" rel="noopener noreferrer">
                                                    www.oanda.com
                                                </a>
                                                and use the Projects Start Date as reference
                                            </React.Fragment>
                                        }>
                                            <IconButton>
                                                <Info />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                                inputProps: {
                                    pattern: "\\d*", // only allow digits
                                },
                            }}
                            error={saveFormData && currentFormData.ProjectValueDollar === ''}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Divider style={{ color: 'lightgrey' }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Person />
                            <Typography variant="h6">Client</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <AutocompleteComponent
                            options={availableOptions.clients}
                            value={selectedOptions.clients !== undefined && selectedOptions.clients !== null ?
                                (selectedOptions.clients[0] === null ?
                                    undefined : selectedOptions.clients) : []}
                            handleChange={handleClientChange}
                            isMulti={true}
                            label="Client Selection"
                            disabled={variantSelected !== 'Englisch' && type==='update'}
                            error={saveFormData && selectedOptions.clients.length === 0}
                        />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <TextField fullWidth InputLabelProps={{ shrink: true }} label="Client Contact" name="ClientContact"
                            value={currentFormData?.ClientContact || ''}
                            onChange={handleInputChange} required multiline rows={3} InputProps={{ readOnly: !isEditMode }}
                            error={saveFormData && currentFormData.ClientContact === ''} disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField InputLabelProps={{ shrink: true }} label="Origin Of Funding" name="OriginOfFunding"
                            value={currentFormData?.OriginOfFunding || ''} onChange={handleInputChange} required multiline rows={3} InputProps={{ readOnly: !isEditMode }}
                            error={saveFormData && currentFormData.OriginOfFunding === ''} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Divider style={{ color: 'lightgrey' }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Public />
                            <Typography variant="h6">Country</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <AutocompleteComponent
                            options={availableOptions.countries.length > 0 ? availableOptions.countries.map((country) => country) : []}
                            value={selectedOptions.countries.length > 0 ? selectedOptions.countries : []}
                            handleChange={handleCountryChange}
                            isMulti={true}
                            label="Countries"
                            disabled={variantSelected !== 'Englisch' && type==='update'}
                            error={saveFormData && selectedOptions.countries.length === 0}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField InputLabelProps={{ shrink: true }} label="Location within the country" name="Location"
                            value={currentFormData?.Location || ''} onChange={handleInputChange} InputProps={{ readOnly: !isEditMode }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Divider style={{ color: 'lightgrey' }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <List />
                            <Typography variant="h6">Topic</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <AutocompleteComponent
                            options={availableOptions.fieldsofServices.length > 0 ? availableOptions.fieldsofServices.map((fos) => fos) : []}
                            value={selectedOptions.fieldsofServices.length > 0 ? selectedOptions.fieldsofServices : []}
                            handleChange={handleFoSChange}
                            isMulti={true}
                            label="Fields of Service"
                            disabled={variantSelected !== 'Englisch' && type==='update'}
                            error={saveFormData && selectedOptions.fieldsofServices.length === 0}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="Key Words" name="KeyWords"
                            value={currentFormData?.KeyWords || ''} onChange={handleInputChange} multiline rows={3} InputProps={{ readOnly: !isEditMode }}
                            disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Divider style={{ color: 'lightgrey' }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Apartment />
                            <Typography variant="h6">Association</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControlLabel control={<Checkbox size='small' checked={currentFormData?.Lead} indeterminate={currentFormData?.Lead === undefined}
                            style={{ color: saveFormData && currentFormData.Lead === undefined ? 'red' : 'inherit' }}
                            onChange={handleCheckboxChange} name="Lead" />} label="INT Lead" required disabled={!isEditMode || (variantSelected !== 'Englisch' && type==='update')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="Associated Firms"
                            name="AssociatedFirms" value={currentFormData?.AssociatedFirms || ''} onChange={handleInputChange}
                            multiline rows={3} InputProps={{ readOnly: !isEditMode }} disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="Proportion carried out by legal entity %" type="number"
                            name="ProportionLegalEntity" value={currentFormData?.ProportionLegalEntity || ''} onChange={handleInputChange}
                            multiline rows={1} InputProps={{ readOnly: !isEditMode }} disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Divider style={{ color: 'lightgrey' }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Groups />
                            <Typography variant="h6">Personell</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="Personnel (Profiles)"
                            name="Personnel" value={currentFormData?.Personnel || ''} onChange={handleInputChange} required multiline rows={6}
                            InputProps={{ readOnly: !isEditMode }} error={saveFormData && currentFormData.Personnel === ''} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="Personnel (EU EOI)"
                            name="Personnel_EU_EOI" value={currentFormData?.Personnel_EU_EOI || ''} onChange={handleInputChange} multiline
                            rows={6} InputProps={{ readOnly: !isEditMode }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="Names and Position Senior Staff"
                            name="SeniorStaff" value={currentFormData?.SeniorStaff || ''} onChange={handleInputChange}
                            required multiline rows={6} InputProps={{ readOnly: !isEditMode }} error={saveFormData && currentFormData.SeniorStaff === ''} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="No of Staff (international/national)"
                            name="NoOfStaff" value={currentFormData?.NoOfStaff || ''} onChange={handleInputChange} required multiline rows={3}
                            InputProps={{ readOnly: !isEditMode }} error={saveFormData && currentFormData.NoOfStaff === ''} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="No of Person Month (international/national)"
                            name="NoOfPersonMonth" value={currentFormData?.NoOfPersonMonth || ''} onChange={handleInputChange} required
                            multiline rows={3} InputProps={{ readOnly: !isEditMode }} error={saveFormData && currentFormData.NoOfPersonMonth === ''} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="No of Person Month by INT (international/national)"
                            name="NoOfPersonMonthINT" value={currentFormData?.NoOfPersonMonthINT || ''} onChange={handleInputChange}
                            InputProps={{ readOnly: !isEditMode }} disabled={variantSelected !== 'Englisch' && type==='update'} />
                        <TextField style={{ marginTop: 20 }} fullWidth={true} InputLabelProps={{ shrink: true }}
                            label="No of Person Month by Associated Firms (international/national)" name="NoOfPersonMonthsByAssociatedFirms"
                            value={currentFormData?.NoOfPersonMonthsByAssociatedFirms} onChange={handleInputChange}
                            multiline rows={3} InputProps={{ readOnly: !isEditMode }} disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Divider style={{ color: 'lightgrey' }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Add />
                            <Typography variant="h6">Additional</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControlLabel control={<Checkbox size='small' checked={currentFormData?.CoP} indeterminate={currentFormData?.CoP === undefined}
                            onChange={handleCheckboxChange} name="CoP" disabled={!isEditMode || (variantSelected !== 'Englisch' && type==='update')} />} label="CoP" />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <TextField fullWidth={true} InputLabelProps={{ shrink: true }} label="Remarks"
                            name="Bemerkungen" value={currentFormData?.Bemerkungen || ''} onChange={handleInputChange} multiline rows={4} InputProps={{ readOnly: !isEditMode }} />

                    </Grid>
                    <Grid item xs={12} md={5}>
                        <TextField InputLabelProps={{ shrink: true }} label="Reference Folder Path"
                            name="Folder" value={currentFormData?.Folder || ''} onChange={handleInputChange} InputProps={{ readOnly: !isEditMode }}
                            disabled={variantSelected !== 'Englisch'} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Divider style={{ color: 'lightgrey' }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Home />
                            <Typography variant="h6">Homepage</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControlLabel control={<Checkbox size='small' checked={currentFormData?.Homepage} indeterminate={currentFormData?.Homepage === undefined}
                            onChange={handleCheckboxChange} disabled={!isEditMode || (variantSelected !== 'Englisch' && type==='update')} name="Homepage" />} label="Available on Homepage" />
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <AutocompleteComponent
                            options={availableOptions.countries.length > 0 ? availableOptions.countries.map((country) => country) : []}
                            value={selectedOptions.countries_homepage.length > 0 ? selectedOptions.countries_homepage : []}
                            handleChange={handleCountryHomepageChange}
                            isMulti={true}
                            label="Country Homepage"
                            disabled={!isEditMode || variantSelected !== 'Englisch'}
                            error={saveFormData && currentFormData.country_homepage === '' && currentFormData?.Homepage === true}
                        />
                    </Grid>
                </Grid>
                <ReferenceVersion open={drawerOpen} onClose={closeDrawer} formData={formData} setCurrentFormData={setCurrentFormData} currentFormData={currentFormData}
                    handleVersionChange={handleVersionChange} toggleEditMode={toggleEditMode} setIsNewVariant={setIsNewVariant} setVariantSelected={setVariantSelected} />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
                <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }} paddingTop={3}>
                    <Grid item xs={12} md={12}>
                        <TextField label="Project Background" name="ProjectBackground" value={currentFormData?.ProjectBackground || ''}
                            onChange={handleInputChange} multiline rows={6} fullWidth InputProps={{ readOnly: !isEditMode }}
                            required error={saveFormData && currentFormData.ProjectBackground === ''} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TextField label="Services Provided" name="ServicesProvided" value={currentFormData?.ServicesProvided || ''}
                            onChange={handleInputChange} multiline rows={6} fullWidth InputProps={{ readOnly: !isEditMode }}
                            required error={saveFormData && currentFormData.ServicesProvided === ''} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TextField label="Project Objectives" name="ProjectObjectives" value={currentFormData?.ProjectObjectives || ''}
                            onChange={handleInputChange} multiline rows={6} fullWidth InputProps={{ readOnly: !isEditMode }}
                            required error={saveFormData && currentFormData.ProjectObjectives === ''} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TextField label="Project Results" name="ProjectResults" value={currentFormData?.ProjectResults || ''}
                            onChange={handleInputChange} multiline rows={6} fullWidth InputProps={{ readOnly: !isEditMode }}
                            required error={saveFormData && currentFormData.ProjectResults === ''} />
                    </Grid>
                </Grid>
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
                <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }} paddingTop={3}>
                    <Grid item xs={12} md={12}>
                        <TextField label="Project Description Short" name="ProjectDescription_short" InputLabelProps={{ shrink: true }}
                            value={currentFormData?.ProjectDescription_short || ''} onChange={handleInputChange} multiline rows={4} fullWidth
                            InputProps={{ readOnly: !isEditMode }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TextField label="Project Description Short German" name="ProjectDescription_short_german" InputLabelProps={{ shrink: true }}
                            value={currentFormData?.ProjectDescription_short_german || ''} onChange={handleInputChange} multiline rows={4} fullWidth
                            InputProps={{ readOnly: !isEditMode }} disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TextField label="Extra Short GTZ PQ" name="extrashort_GTZ_PQ" value={currentFormData?.extrashort_GTZ_PQ || ''} InputLabelProps={{ shrink: true }}
                            onChange={handleInputChange} multiline rows={4} fullWidth InputProps={{ readOnly: !isEditMode }} />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TextField label="Extra Short GTZ German" name="extrashort_GTZ_PQ_german" value={currentFormData?.extrashort_GTZ_PQ_german || ''}
                            InputLabelProps={{ shrink: true }}
                            onChange={handleInputChange} multiline rows={4} fullWidth InputProps={{ readOnly: !isEditMode }} disabled={variantSelected !== 'Englisch' && type==='update'} />
                    </Grid>
                </Grid>
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
                <Grid item md={12}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <People />
                        <Typography variant="h6">Experts</Typography>
                    </Stack>
                </Grid>
                {type === 'update' && currentFormData?.ProjectID ? (
                    <Grid container spacing={3} margin={2}>
                        <EditableDataGrid
                            col={referenceExpertGridDefinition}
                            rows={linkedExperts}
                            editIsOn={isEditMode}
                            formData={{ linkedExperts }}
                            setFormData={handleExpertsChange}
                            dataPath="linkedExperts"
                            preselectionIsOn={true}
                            preselectionDisplayField="name"
                            preselectionValueField="id"
                            readOnlyFields={['name']}
                            preselectionSearchFn={searchAvailableExperts}
                            preselectionSearchLabel="Search for an expert by name"
                            customFieldRenderers={expertFieldRenderers}
                        />
                    </Grid>
                ) : (
                    <Typography color="textSecondary" sx={{ p: 2 }}>
                        Save the reference before linking experts.
                    </Typography>
                )}
            </TabPanel>
            <TabPanel value={tabValue} index={4}>
                <Grid item md={12}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Groups />
                        <Typography variant="h6">Partners</Typography>
                    </Stack>
                </Grid>
                {type === 'update' && currentFormData?.ProjectID ? (
                    <Grid container spacing={3} margin={2}>
                        <EditableDataGrid
                            col={referencePartnerGridDefinition}
                            rows={linkedPartners}
                            editIsOn={isEditMode}
                            formData={{ linkedPartners }}
                            setFormData={handlePartnersChange}
                            dataPath="linkedPartners"
                            preselectionIsOn={true}
                            preselectionDisplayField="name"
                            preselectionValueField="id"
                            readOnlyFields={['name']}
                            preselectionSearchFn={searchAvailablePartners}
                            preselectionSearchLabel="Search for a partner by name"
                        />
                    </Grid>
                ) : (
                    <Typography color="textSecondary" sx={{ p: 2 }}>
                        Save the reference before linking partners.
                    </Typography>
                )}
            </TabPanel>
            <TabPanel value={tabValue} index={5}>
                {type === 'update' && currentFormData?.ProjectID ? (
                    <ReferenceDocumentsTab refId={currentFormData.ProjectID} active={tabValue === 5} />
                ) : (
                    <Typography color="textSecondary" sx={{ p: 2 }}>
                        Save the reference before adding documents.
                    </Typography>
                )}
            </TabPanel>
        </div>
    );
}


function TabPanel(props: any) {
    const { children, value, index } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: any) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export default ReferenceForm;